import {
  Field,
  FieldObjectType,
  SingleAndMultiSelectFieldOption,
} from 'module/api/field/types';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { Button, Drawer, Textarea } from '@rocketlane/ui';
import { Col, InputRef, Row } from 'antd';
import { startCase } from 'lodash';
import {
  useCreateFieldMutation,
  useUpdateFieldMutation,
  useGetFieldSectionsQuery,
} from 'queries/fields';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { FieldTypeSelect } from './field-type-select';
import { OptionList } from './option-list';
import { getFieldTypeFromUiType, getUiFieldType } from '../fields/field-types';
import { UiFieldType } from '../fields/types';
import { isReasonField, isSelectTypeField } from '../fields/utils';

interface FieldFormProps {
  fieldLabel: string;
  fieldType: UiFieldType;
  description: string;
  options?: SingleAndMultiSelectFieldOption[];
}
const payloadFromFieldForm = (formValue: FieldFormProps, field?: Field) => ({
  fieldLabel: formValue.fieldLabel,
  description: formValue.description,
  ...getFieldTypeFromUiType(formValue.fieldType),
  metaData: isSelectTypeField(formValue.fieldType)
    ? {
        options: formValue.options ?? [],
        ...field?.metaData,
      }
    : field?.metaData,
});

interface Props {
  field?: Field;
  fieldObjectType: FieldObjectType;
  visible: boolean;
  onClose: VoidFunction;
}

const FieldMapping = {
  [FieldObjectType.Signal]: 'signal_fields',
  [FieldObjectType.Meeting]: 'meeting_fields',
  [FieldObjectType.Email]: 'email_fields',
};

export const ManageFieldDrawer: FC<Props> = ({
  visible,
  onClose,
  field,
  fieldObjectType,
}) => {
  const isEdit = field != null;
  const requiredMessage = useTranslation('input.required');
  const [form] = Form.useForm<FieldFormProps>();
  const { mutateAsync: updateFieldApi, isPending: isUpdateFieldPending } =
    useUpdateFieldMutation();
  const { mutateAsync: createFieldApi, isPending: isCreateFieldPending } =
    useCreateFieldMutation();

  // Fetch field sections for the current field object type
  const { data: fieldSections, isLoading: isLoadingFieldSections } =
    useGetFieldSectionsQuery(fieldObjectType);

  const fieldType = FieldMapping[fieldObjectType] ?? 'signal_fields';
  const title = useTranslation(
    `settings.${fieldType}.${isEdit ? 'edit' : 'create'}_drawer.title` as any,
  );

  const manageOptionInstructionDrawerProps = useMemo(
    () => ({
      title: (
        <FormattedMessage id="settings.signal_fields.option_instructions_drawer.title" />
      ),
      breadcrumbs: [{ title }],
    }),
    [title],
  );
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (visible) {
      inputRef.current?.focus();
    }
    if (isEdit) {
      form.setFieldsValue({
        fieldLabel: field.fieldLabel ?? startCase(field.fieldName),
        fieldType: getUiFieldType(field),
        description: field.description,
        options: field.metaData?.options,
      });
    } else {
      form.setFieldsValue({
        fieldLabel: '',
        fieldType: UiFieldType.Text,
        description: '',
        options: [],
      });
    }
  }, [field, form, isEdit, visible]);
  const onSave = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        if (isEdit) {
          updateFieldApi({
            fieldId: field.fieldId,
            fieldSectionId:
              fieldSections?.find((section) => section.default === true)
                ?.fieldSectionId ?? 0,
            payload: payloadFromFieldForm(values, field),
          })
            .then(() => onClose())
            .catch(() => {});
        } else {
          createFieldApi({
            isEnabled: true,
            objectType: fieldObjectType,
            ...payloadFromFieldForm(values),
            fieldSectionId:
              fieldSections?.find((section) => section.default === true)
                ?.fieldSectionId ?? 0,
            private: true,
          })
            .then(() => onClose())
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [
    createFieldApi,
    field,
    fieldObjectType,
    form,
    isEdit,
    fieldSections,
    onClose,
    updateFieldApi,
  ]);

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={664}
      title={title}
      content={
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                name="fieldLabel"
                label={
                  <FormattedMessage id="settings.signal_fields.form.field_label.label" />
                }
                required
                rules={[{ required: true, message: requiredMessage }]}
              >
                <Input
                  ref={inputRef}
                  placeholder={useTranslation(
                    'settings.signal_fields.form.field_label.placeholder',
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="fieldType"
                label={
                  <FormattedMessage id="settings.signal_fields.form.field_type.label" />
                }
                disabled={isEdit}
              >
                <FieldTypeSelect isDisabled={isEdit} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="description"
            label={<FormattedMessage id="description" />}
            extra={
              <FormattedMessage id="settings.signal_fields.form.description.help" />
            }
          >
            <Textarea rows={3} maxRows={5} />
          </Form.Item>
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const fieldType = getFieldValue('fieldType') as UiFieldType;
              const reasonField = isEdit && isReasonField(field);

              return (
                <Form.Item
                  name="options"
                  label={<FormattedMessage id="choices" />}
                  hidden={!isSelectTypeField(fieldType) || reasonField}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (
                          !reasonField &&
                          isSelectTypeField(fieldType) &&
                          !value.length
                        ) {
                          return Promise.reject(new Error(''));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <OptionList
                    manageOptionInstructionDrawerProps={
                      manageOptionInstructionDrawerProps
                    }
                    addedAsFormItem
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      }
      footer={
        <>
          <Button onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            loading={
              isUpdateFieldPending ||
              isCreateFieldPending ||
              isLoadingFieldSections
            }
          >
            <FormattedMessage id={isEdit ? 'update' : 'save'} />
          </Button>
        </>
      }
    />
  );
};
